import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap';
import PropTypes from 'prop-types';
import {getTenantErrorMessage, tenantsApi} from '../services/tenantsApi';
import CopyableCommand from '../../../shared/ui/CopyableCommand';
import {useEventGridProvisioningEnabled} from '../../../shared/constants/featureFlags';

const CreateEdgeModal = ({show, onHide}) => {
  const eventGridProvisioningEnabled = useEventGridProvisioningEnabled();
  const [id, setId] = useState('');
  const [address, setAddress] = useState('');
  const [geolocation, setGeolocation] = useState(null);
  const [nameParts, setNameParts] = useState({country: '', postal: '', street: ''});
  const [balenaUuid, setBalenaUuid] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createdEdge, setCreatedEdge] = useState(null);

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  const generatedName = id && nameParts.street
    ? `${id}-${nameParts.country}-${nameParts.postal}-${nameParts.street}`.replace(/\s+/g, '')
    : '';

  const initAutocomplete = useCallback(() => {
    if (!inputRef.current || !globalThis.google?.maps?.places) return;
    if (autocompleteRef.current) return;

    autocompleteRef.current = new globalThis.google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: {country: 'ch'}
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (!place.geometry) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setGeolocation({lat, lng});
      setAddress(place.formatted_address || inputRef.current.value);

      // Extract country, postal code, and street from address components
      const components = place.address_components || [];
      const route = components.find(c => c.types.includes('route'));
      const streetNumber = components.find(c => c.types.includes('street_number'));
      const postalCode = components.find(c => c.types.includes('postal_code'));
      const country = components.find(c => c.types.includes('country'));
      const street = (route?.long_name || '') + (streetNumber?.long_name || '');
      setNameParts({
        country: country?.short_name || '',
        postal: postalCode?.long_name || '',
        street
      });
    });
  }, []);

  useEffect(() => {
    if (show) {
      // Small delay to ensure the input is rendered
      const timer = setTimeout(initAutocomplete, 100);
      return () => clearTimeout(timer);
    } else {
      autocompleteRef.current = null;
    }
  }, [show, initAutocomplete]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const result = await tenantsApi.createEdge({
      id,
      name: generatedName,
      address,
      geolocation,
      ...(eventGridProvisioningEnabled ? {} : {balenaUuid})
    });

    setLoading(false);

    if (!result.ok) {
      setError(getTenantErrorMessage(result.error, 'Failed to create edge.'));
      return;
    }

    // Event Grid edges return an accessKey/dockerCommand and get a result
    // step; legacy Balena edges close immediately as before.
    if (eventGridProvisioningEnabled && result.data?.dockerCommand) {
      setCreatedEdge(result.data);
      return;
    }

    handleClose();
  };

  const handleClose = () => {
    setId('');
    setAddress('');
    setGeolocation(null);
    setNameParts({country: '', postal: '', street: ''});
    setBalenaUuid('');
    setError(null);
    setCreatedEdge(null);
    autocompleteRef.current = null;
    onHide();
  };

  const isValid = eventGridProvisioningEnabled
    ? id && address && generatedName
    : id && address && balenaUuid && generatedName;

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{createdEdge ? 'SCC Created' : 'Create SCC'}</Modal.Title>
      </Modal.Header>

      {createdEdge ? (
        <>
          <Modal.Body>
            <Alert variant="success" className="mb-3">
              SCC <strong>{createdEdge.edgeId}</strong> was created successfully.
            </Alert>
            {createdEdge.dockerCommand && (
              <>
                <p className="mb-2" style={{fontSize: 14}}>
                  Run this command on the device to start the SCC core. The access
                  key is shown only here — copy and store it now.
                </p>
                <CopyableCommand value={createdEdge.dockerCommand} label="docker run command"/>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleClose}>Done</Button>
          </Modal.Footer>
        </>
      ) : (
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>ID</Form.Label>
              <Form.Control
                autoFocus
                type="text"
                value={id}
                onChange={(e) => setId(e.target.value)}
                placeholder="e.g. 3001"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                ref={inputRef}
                type="text"
                placeholder="Search address..."
                defaultValue={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  setGeolocation(null);
                  setNameParts({country: '', postal: '', street: ''});
                }}
                required
              />
              {geolocation && (
                <Form.Text className="text-muted">
                  {geolocation.lat.toFixed(4)}° N, {geolocation.lng.toFixed(4)}° E
                </Form.Text>
              )}
            </Form.Group>

            {!eventGridProvisioningEnabled && (
              <Form.Group className="mb-3">
                <Form.Label>Balena UUID</Form.Label>
                <Form.Control
                  type="text"
                  value={balenaUuid}
                  onChange={(e) => setBalenaUuid(e.target.value)}
                  placeholder="e.g. b597644..."
                  required
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Name (auto-generated)</Form.Label>
              <Form.Control
                type="text"
                value={generatedName}
                disabled
                placeholder="<ID>-<Street/Nr>"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!isValid || loading}>
              {loading ? <><Spinner size="sm" className="me-1"/>Creating...</> : 'Create SCC'}
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
};

CreateEdgeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired
};

export default CreateEdgeModal;
